import {useEffect, useState} from "react"
import {Typography, Box, Grid, Paper, Tabs, Tab, Chip, Avatar, CircularProgress, IconButton} from "@mui/material"
import {
    Email as EmailIcon,
    Verified as VerifiedIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    CalendarToday as CalendarTodayIcon,
    PhotoCamera as PhotoCameraIcon,
    Person as PersonIcon,
    SupervisorAccount as SupervisorAccountIcon,
    ShoppingCart as ShoppingCartIcon,
    BarChart as BarChartIcon,
    Settings as SettingsIcon,
    LocalPharmacy as LocalPharmacyIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import TabAdminUsers from "./admin-tabs/tab-admin-users"
import TabAdminDistributorsPharmacies from "./admin-tabs/tab-admin-distributors-pharmacies"
import TabAdminOrders from "./admin-tabs/tab-admin-orders"
import TabAdminAnalytics from "./admin-tabs/tab-admin-analytics"
import TabAdminSettings from "./admin-tabs/tab-admin-settings"
import {PageContainer} from "../components/common/page-container"
import axiosInstance from "../config/axios-config";
import {GetLogo, UploadLogo} from "../profile/main/api/photo-data-handler";
import {GetTenantData} from "../profile/main/api/get-tenant-data";

export default function AdminManagement({navOpen, tenantId, tenantName, tenantType}) {
    const [loading, setLoading] = useState(true)
    const [admin, setAdmin] = useState(null)
    const [logoUrl, setLogoUrl] = useState("/placeholder.svg?height=120&width=120")
    const [tabValue, setTabValue] = useState(0)
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalDistributors: 0,
        totalPharmacies: 0,
        totalOrders: 0,
        totalRevenue: 0,
    })
    const {t} = useTranslation()

    const fetchAdminData = async () => {
        setLoading(true)
        try {
            const response = await axiosInstance.get(`/api/v1/tenants/${tenantId}`);
            setAdmin(response.data)
            if (admin?.imageUrl) {
                const url = await GetLogo({filename: admin?.imageUrl})
                setLogoUrl(url)
            }
        } catch (error) {
            console.error("Error fetching admin data:", error)
            // Fallback data
            setAdmin({
                name: "Admin",
                surname: "User",
                email: "admin@xmed.am",
                role: "SUPER_ADMIN",
                createdAt: "2023-01-01",
            })
        }
        // Fetch platform stats
        setLoading(false)
    }

    const fetchStats = async () => {
        setLoading(true);

        try {
            const [pharmaciesRes, distributorsRes, usersRes] = await Promise.all([
                axiosInstance.get(`/api/v1/tenants`, {
                    params: { tenantType: "PHARMACY" },
                }),
                axiosInstance.get(`/api/v1/tenants`, {
                    params: { tenantType: "IMPORTER" },
                }),
                axiosInstance.get("/api/v1/users"),
            ]);

            setStats(prev => ({
                ...prev,
                totalPharmacies: pharmaciesRes.data?.length || 0,
                totalDistributors: distributorsRes.data?.length || 0,
                totalUsers: usersRes.data?.length || 0,
            }));

        } catch (error) {
            console.error("Error fetching admin data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdminData().then(r => {
            console.log(r)
        })
        fetchStats().then(r => {
            console.log(r)
        })
    }, [tenantId, admin?.imageUrl])

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue)
    }

    const handleFileChange = async (event) => {
        const selectedFile = event.target.files[0];

        if (selectedFile) {
            try {
                const response = await UploadLogo({id: tenantId, file: selectedFile});
                console.log("Uploaded file response:", response.data);
                await GetTenantData({tenantId: tenantId}).then(data => {
                    setAdmin(data);
                });
                // Immediately update the photo URL
                const newPhotoUrl = await GetLogo({filename: admin.imageUrl});
                setLogoUrl(newPhotoUrl);
            } catch (error) {
                console.error("Upload failed:", error);
            }
        }
    };

    const StatCard = ({icon, label, value, color}) => (
        <Paper
            elevation={1}
            sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderLeft: `4px solid`,
                borderLeftColor: color || "primary.main",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 4,
                },
            }}
        >
            <Box
                sx={{
                    bgcolor: `${color || "primary.main"}15`,
                    borderRadius: "50%",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {icon}
            </Box>
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
                <Typography variant="h5" sx={{fontWeight: 600}}>
                    {value}
                </Typography>
            </Box>
        </Paper>
    )

    return (
        <PageContainer component="main" navOpen={navOpen}>
            <Box sx={{minHeight: "100vh", bgcolor: "background.default"}} className="admin-management-container">
                <Box sx={{display: "flex", alignItems: "center", mb: 1, p: 1}} />

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                        <CircularProgress />
                    </Box>
                ) : admin ? (
                    <>
                        {/* Admin Header */}
                        <Paper elevation={2} sx={{mb: 3, overflow: "hidden", mx: 0}}>
                            <Box
                                sx={{
                                    p: {xs: 2, md: 3},
                                    background: "linear-gradient(to right, #f5f7fa, #e4e8f0)",
                                    display: "flex",
                                    flexDirection: {xs: "column", md: "row"},
                                    alignItems: {xs: "center", md: "flex-start"},
                                }}
                            >
                                <Box sx={{position: "relative", mr: {xs: 0, md: 4}, mb: {xs: 2, md: 0}}}>
                                    <Avatar
                                        src={logoUrl}
                                        alt={admin.tenantNameEn}
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                            bgcolor: "primary.main",
                                            fontSize: "2.5rem",
                                        }}
                                    >
                                        {admin.tenantNameEn?.charAt(0)}
                                    </Avatar>
                                    <IconButton
                                        component="label"
                                        sx={{
                                            position: "absolute",
                                            bottom: 0,
                                            right: 0,
                                            bgcolor: "primary.main",
                                            color: "white",
                                            "&:hover": {bgcolor: "primary.dark"},
                                        }}
                                        size="small"
                                    >
                                        <PhotoCameraIcon fontSize="small" />
                                        <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                                    </IconButton>
                                </Box>
                                <Box sx={{flex: 1, minWidth: 0, width: "100%"}}>
                                    <Box sx={{display: "flex", alignItems: "center", flexWrap: "wrap"}}>
                                        <Typography variant="h4" component="h1" sx={{fontWeight: 500, mr: 1}}>
                                            {admin.tenantNameEn}
                                        </Typography>
                                        <VerifiedIcon color="primary" sx={{fontSize: 24}} />
                                        <Chip
                                            icon={<AdminPanelSettingsIcon />}
                                            label={t("Admin") || "Admin"}
                                            color="error"
                                            sx={{ml: {xs: 0, sm: 2}, mt: {xs: 1, sm: 0}}}
                                        />
                                    </Box>

                                    <Grid container spacing={2} sx={{mt: 2}}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Box sx={{display: "flex", alignItems: "flex-start"}}>
                                                <EmailIcon sx={{color: "text.secondary", mr: 1, mt: 0.3}} />
                                                <Typography variant="body1">{admin.tenantEmail}</Typography>
                                            </Box>
                                        </Grid>
                                        {admin.createdAt && (
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Box sx={{display: "flex", alignItems: "flex-start"}}>
                                                    <CalendarTodayIcon sx={{color: "text.secondary", mr: 1, mt: 0.3}} />
                                                    <Typography variant="body1">
                                                        {t("MemberSince") || "Member Since"}: {new Date(admin.createdAt).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>

                                    {/* Stats Cards */}
                                    <Grid container spacing={2} sx={{mt: 2}}>
                                        <Grid item xs={6} sm={4} md={2.4}>
                                            <StatCard
                                                icon={<PersonIcon sx={{color: "primary.main"}} />}
                                                label={t("Users") || "Total Users"}
                                                value={stats.totalUsers}
                                                color="primary.main"
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={4} md={2.4}>
                                            <StatCard
                                                icon={<SupervisorAccountIcon sx={{color: "#2196f3"}} />}
                                                label={t("Distributors") || "Distributors"}
                                                value={stats.totalDistributors}
                                                color="#2196f3"
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={4} md={2.4}>
                                            <StatCard
                                                icon={<LocalPharmacyIcon sx={{color: "#4caf50"}} />}
                                                label={t("Pharmacies") || "Pharmacies"}
                                                value={stats.totalPharmacies}
                                                color="#4caf50"
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={4} md={2.4}>
                                            <StatCard
                                                icon={<ShoppingCartIcon sx={{color: "#ff9800"}} />}
                                                label={t("Orders") || "Total Orders"}
                                                value={stats.totalOrders}
                                                color="#ff9800"
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={4} md={2.4}>
                                            <StatCard
                                                icon={<BarChartIcon sx={{color: "#9c27b0"}} />}
                                                label={t("Revenue") || "Total Revenue"}
                                                value={`${stats.totalRevenue?.toLocaleString() || 0}֏`}
                                                color="#9c27b0"
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>

                            {/* Tabs Navigation */}
                            <Box sx={{borderBottom: 1, borderColor: "divider"}}>
                                <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    aria-label="admin tabs"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    <Tab
                                        icon={<PersonIcon />}
                                        iconPosition="start"
                                        label={t("UserManagement") || "Users Management"}
                                        id="admin-tab-0"
                                        aria-controls="admin-tabpanel-0"
                                    />
                                    <Tab
                                        icon={<SupervisorAccountIcon />}
                                        iconPosition="start"
                                        label={t("DistributorsPharmacies") || "Distributors & Pharmacies"}
                                        id="admin-tab-1"
                                        aria-controls="admin-tabpanel-1"
                                    />
                                    <Tab
                                        icon={<ShoppingCartIcon />}
                                        iconPosition="start"
                                        label={t("Orders") || "Orders Overview"}
                                        id="admin-tab-2"
                                        aria-controls="admin-tabpanel-2"
                                    />
                                    <Tab
                                        icon={<BarChartIcon />}
                                        iconPosition="start"
                                        label={t("Analytics") || "Analytics Dashboard"}
                                        id="admin-tab-3"
                                        aria-controls="admin-tabpanel-3"
                                    />
                                    <Tab
                                        icon={<SettingsIcon />}
                                        iconPosition="start"
                                        label={t("Settings") || "System Settings"}
                                        id="admin-tab-4"
                                        aria-controls="admin-tabpanel-4"
                                    />
                                </Tabs>
                            </Box>

                            {/* Tab Content */}
                            <Box sx={{p: {xs: 2, md: 3}, minWidth: 0}}>
                                <TabAdminUsers tabValue={tabValue} />
                                <TabAdminDistributorsPharmacies tabValue={tabValue} />
                                <TabAdminOrders tabValue={tabValue} />
                                <TabAdminAnalytics tabValue={tabValue} />
                                <TabAdminSettings tabValue={tabValue} />
                            </Box>
                        </Paper>
                    </>
                ) : (
                    <Typography variant="h6" sx={{textAlign: "center", py: 4}}>
                        {t("AdminNotFound") || "Admin data not found"}
                    </Typography>
                )}
            </Box>
        </PageContainer>
    )
}
